import React, { useState } from "react";

// ========== NMA-BANKRUPTCY: Form 65 Navigator v2.1 ==========
// Epistemic Probe Edition + Save/Load + E-Filing Export
// © 2026 Dr. Olutoyese Oyelese
//
// WHAT'S NEW IN v2.1:
// - Save/Load case files (JSON) for continuity across sessions
// - XML export structured to match OSB E-Filing Form 65 field layout
// - CSV export with OSB type mapping for Ascend/Aryza import
// - Verified 2025 Superintendent's Standards (Directive 11R2-2025 Appendix A)
//
// v2.0 FEATURES:
// - Collapsible probe panels for every form section
// - 43 probe questions grounded in OSB Form 65 Guidance & Directive 11R2-2025
// - Epistemic tagging: Documented / Verbal / Unverified / N/A
// - Probes are INTERNAL ONLY — they do not appear on printed reports
// - Consistency gate uses probe responses to strengthen checks
// - Demo case includes pre-filled probe responses

// ========== SUPERINTENDENT'S STANDARDS — 2025 ==========
// VERIFIED: Directive 11R2-2025, Appendix A (issued March 31, 2025)
// Derived from before-tax LICO for urban areas 500,000+
// 2025 = 2024 LICO + 2.31% CPI adjustment
// Source: https://ised-isde.canada.ca/site/office-superintendent-bankruptcy/en/directive-no-11r2-2025-surplus-income
const LICO = {
  1: { monthly: 2666, label: '1 person' },
  2: { monthly: 3318, label: '2 persons' },
  3: { monthly: 4080, label: '3 persons' },
  4: { monthly: 4953, label: '4 persons' },
  5: { monthly: 5618, label: '5 persons' },
  6: { monthly: 6336, label: '6 persons' },
  7: { monthly: 7054, label: '7+ persons' },
};

// ========== FORM CATEGORIES ==========

const INC = [
  { k: 'employment', l: 'Net Employment Income', h: 'Wages, salaries, commissions, gratuities — net of statutory remittances' },
  { k: 'spouseEmployment', l: 'Spouse/Partner Employment', h: 'Net employment income of spouse/partner' },
  { k: 'selfEmploymentGross', l: 'Self-Employment (Gross)', h: 'Gross business income before deductions — NOT included in totals' },
  { k: 'selfEmployment', l: 'Self-Employment (Net)', h: 'Net after ITA-permitted business expenses and statutory remittances' },
  { k: 'ei', l: 'Employment Insurance', h: 'Net EI benefits received' },
  { k: 'pension', l: 'Pension / Annuities', h: 'CPP, OAS, GIS, employer pension, LTD — net of deductions' },
  { k: 'childSupport', l: 'Child Support Received', h: 'Net support received per court order or agreement' },
  { k: 'spousalSupport', l: 'Spousal Support Received', h: 'Net alimony/alimentary pension received' },
  { k: 'socialAssistance', l: 'Social Assistance / Welfare', h: 'Provincial/territorial assistance payments' },
  { k: 'otherInsurance', l: 'Other Insurance Benefits', h: 'Accident, illness, pregnancy, short-term disability' },
  { k: 'childBenefit', l: 'Child Tax Benefit / CCB', h: 'Canada Child Benefit payments' },
  { k: 'otherGovt', l: 'Other Govt Benefits', h: 'GST/HST credit, disability, workers comp — specify in details' },
  { k: 'otherIncome', l: 'Other Income', h: 'Rental, investment, gig, other — specify in details' },
];

const DED = [
  { k: 'childSupportPaid', l: 'Child Support Paid', h: 'Payments per court order or agreement' },
  { k: 'spousalSupportPaid', l: 'Spousal Support Paid', h: 'Alimony/alimentary pension paid per order' },
  { k: 'childCare', l: 'Child Care', h: 'Net of subsidies/tax benefits — to enable work/school' },
  { k: 'medical', l: 'Medical Expenses', h: 'Clinically-required, net of insurance coverage' },
  { k: 'courtFines', l: 'Court-Imposed Fines/Penalties', h: 'Fines or penalties imposed by a court' },
  { k: 'employmentExpenses', l: 'Employment/Business Expenses', h: 'ITA-permitted, as condition of employment' },
  { k: 'stayLifted', l: 'Debts — Stay Lifted', h: 'Payments where stay of proceedings has been lifted' },
  { k: 'otherNonDisc', l: 'Other Non-Discretionary', h: 'Other applicable payments — specify in details' },
];

const EXP = [
  // Housing
  { k: 'rent', l: 'Rent / Mortgage', h: 'Primary (and secondary) residence financing', cat: 'Housing' },
  { k: 'propTax', l: 'Property Tax / Condo Fees', h: 'Municipal tax, education tax, strata fees', cat: 'Housing' },
  { k: 'heating', l: 'Heating / Gas / Oil', h: 'Heating fuel for residence', cat: 'Housing' },
  { k: 'phone', l: 'Phone / Cell Phone', h: 'Landline and cellular including data', cat: 'Housing' },
  { k: 'cable', l: 'Cable / Streaming', h: 'TV, satellite, streaming subscriptions', cat: 'Housing' },
  { k: 'internet', l: 'Internet', h: 'Internet service (exclude cell data)', cat: 'Housing' },
  { k: 'electricity', l: 'Electricity', h: 'Hydro/electrical for residence', cat: 'Housing' },
  { k: 'water', l: 'Water', h: 'Municipal water service', cat: 'Housing' },
  { k: 'furniture', l: 'Furniture', h: 'Rent, purchase, or maintain furnishings', cat: 'Housing' },
  { k: 'housingOther', l: 'Housing — Other', h: 'Other shelter needs — specify in details', cat: 'Housing' },
  // Living
  { k: 'food', l: 'Food / Grocery', h: 'Groceries and meal preparation', cat: 'Living' },
  { k: 'laundry', l: 'Laundry / Dry Cleaning', h: 'Cleaning and mending clothing', cat: 'Living' },
  { k: 'grooming', l: 'Grooming / Toiletries', h: 'Hygiene, haircare, personal care products', cat: 'Living' },
  { k: 'clothing', l: 'Clothing', h: 'Clothing and accessories for family unit', cat: 'Living' },
  // Transportation
  { k: 'vehiclePmt', l: 'Vehicle Lease/Payments', h: 'Lease or financing payments on vehicles', cat: 'Transport' },
  { k: 'vehicleIns', l: 'Vehicle Insurance/Registration', h: 'Mandatory and supplementary vehicle insurance', cat: 'Transport' },
  { k: 'vehicleMaint', l: 'Repair / Maintenance / Gas', h: 'Fuel, maintenance, repairs for vehicles', cat: 'Transport' },
  { k: 'publicTransit', l: 'Public Transportation', h: 'Transit, taxi, commuter rail', cat: 'Transport' },
  // Personal
  { k: 'tobacco', l: 'Tobacco / Vaping / Cannabis', h: 'Tobacco, cannabis, non-medical drugs', cat: 'Personal' },
  { k: 'alcohol', l: 'Alcohol', h: 'Beer, wine, liquor from any source', cat: 'Personal' },
  { k: 'restaurants', l: 'Meals / Restaurants', h: 'Eating out, prepared meals', cat: 'Personal' },
  { k: 'entertainment', l: 'Entertainment / Sports', h: 'Movies, concerts, sports, gambling, gym', cat: 'Personal' },
  { k: 'gifts', l: 'Gifts / Charitable Donations', h: 'Gifts and donations to non-relatives', cat: 'Personal' },
  { k: 'allowances', l: 'Allowances', h: 'Payments to relatives or dependants', cat: 'Personal' },
  // Insurance
  { k: 'insHome', l: 'Home Insurance', h: 'Fire, disaster, optional mortgage insurance', cat: 'Insurance' },
  { k: 'insContents', l: 'Contents Insurance', h: 'Theft, fire, liability for home contents', cat: 'Insurance' },
  { k: 'insLife', l: 'Life Insurance', h: 'Death, injury, disability coverage', cat: 'Insurance' },
  // Payments
  { k: 'pmtEstate', l: 'Payments to Estate', h: 'Above required surplus — incl. s.156.1 agreements', cat: 'Payments' },
  { k: 'pmtSecured', l: 'To Secured Creditor', h: 'Secured debt (not mortgage or vehicle)', cat: 'Payments' },
  { k: 'pmtFinAdvice', l: 'Financial Advice Provider', h: 'Payments to advisors other than LIT — must detail', cat: 'Payments' },
  { k: 'pmtOther', l: 'Other Payments', h: 'Other planned/committed payments — specify', cat: 'Payments' },
];

// ========== PROBE QUESTIONS ==========
// Grounded in: OSB Form 65 Completion Guidance (2025-04-02)
//              Directive 11R2-2025 Surplus Income
//              OSB Compliance Program monitoring priorities
//
// Epistemic tags: DOC (documented), VRB (verbal), UNV (unverified), NA (not applicable)

const PROBES = {
  family: {
    title: 'Family Unit Verification',
    ref: 'Directive 11R2-2025 ¶4, Form 65 Guidance — Family Unit',
    questions: [
      {
        id: 'FU1',
        q: 'How many people currently reside in the household? List each person and their relationship to the debtor.',
        why: 'Family unit size directly determines the LICO threshold. Each person must be accounted for.',
        ref: '11R2 ¶4',
      },
      {
        id: 'FU2',
        q: 'Does anyone in the household who is NOT a dependant contribute to household expenses (rent, food, utilities)?',
        why: 'A person who contributes to expenses may be a family unit member even if not a dependant. Conversely, a roommate paying separate rent may not be.',
        ref: '11R2 ¶4',
      },
      {
        id: 'FU3',
        q: 'Is there a spouse or common-law partner? If yes, are they fully cooperating with income and expense disclosure?',
        why: 'If a spouse refuses to divulge, the LIT must document: the relationship, what information was withheld, and how calculations were adjusted. Non-bankrupt spouse refusal does NOT exclude them from the family unit.',
        ref: '11R2 ¶6(2)(3)',
      },
      {
        id: 'FU4',
        q: 'Are there children in shared custody who split time between this household and another?',
        why: 'Children not residing full-time are still family unit members if they benefit from or contribute to the household. Shared custody affects CCB entitlement and expense allocation.',
        ref: '11R2 ¶4 — non-resident dependants',
      },
      {
        id: 'FU5',
        q: 'Has anyone joined or left the household since the last reporting period?',
        why: 'Changes to family unit composition require an amended Form 65 and recalculation of surplus income obligation.',
        ref: 'BIA s.68(3)(a) — material change',
      },
      {
        id: 'FU6',
        q: 'Is any family unit member (other than a non-bankrupt spouse) refusing or neglecting to divulge income or expenses?',
        why: 'A non-spouse who refuses disclosure is deemed NOT a member of the family unit. This reduces the LICO threshold (smaller family size) and must be documented with specific details.',
        ref: '11R2 ¶6(3)',
      },
      {
        id: 'FU7',
        q: 'Does the debtor or spouse work away from home on rotation (e.g., remote work camps, fly-in/fly-out)?',
        why: 'A spouse not residing in the household while employed as a remote rotational worker is still a family unit member.',
        ref: 'Form 65 Guidance — Family Unit',
      },
    ],
  },

  income: {
    title: 'Income Verification',
    ref: 'Directive 11R2-2025 ¶5, Form 65 Guidance — Monthly Income Table',
    questions: [
      // Employment
      {
        id: 'INC1',
        q: 'Has the debtor provided pay stubs or employer statements for the full reporting period?',
        why: 'Pay stubs are primary evidence for employment income. They also verify statutory deductions (tax, CPP, EI) independently.',
        ref: 'Form 65 Guidance — Net employment income',
      },
      {
        id: 'INC2',
        q: 'Is income consistent month-to-month, or does it vary due to commission, overtime, seasonal work, or shift differentials?',
        why: 'Variable income must be averaged over a representative period. The LIT must use professional judgement to determine the appropriate averaging period.',
        ref: '11R2 ¶8(2) — average monthly income',
      },
      {
        id: 'INC3',
        q: 'Does the debtor receive any income in cash — tips, side jobs, gig work, informal employment — that would not appear on pay stubs or T4s?',
        why: 'Cash income is the most common source of unreported income. It affects total income and surplus calculation. BIA s.68(2) defines total income broadly.',
        ref: 'BIA s.68(2) — total income',
      },
      {
        id: 'INC4',
        q: 'Has the debtor changed jobs, been promoted, had hours reduced, or been laid off during this period?',
        why: 'Job changes are material changes requiring an amended Form 65. A change that moves income above/below surplus threshold has significant consequences.',
        ref: 'BIA s.68(3)(a) — material change',
      },
      // Self-Employment
      {
        id: 'INC5',
        q: 'If self-employed: has the debtor provided business bank statements, bookkeeping records, or tax filings?',
        why: 'Self-employment requires BOTH gross and net income. Only deductions permitted by the Income Tax Act may be claimed. The LIT must verify what has actually been paid in remittances.',
        ref: '11R2 ¶5(2)(b) — self-employment deductions',
      },
      {
        id: 'INC6',
        q: 'If self-employed: are personal expenses being paid through the business (personal phone, vehicle, meals)?',
        why: 'Personal expenses flowing through a business inflate deductions and reduce reported net income. Only ITA-permitted business expenses are deductible.',
        ref: '11R2 ¶5(2)(b)',
      },
      // Spouse / Family
      {
        id: 'INC7',
        q: 'Has the spouse or partner provided independent documentation of their income (their own pay stubs, NOA, benefit statements)?',
        why: 'Spouse income directly affects surplus calculation through the family unit total. Independent verification prevents both over- and under-reporting.',
        ref: '11R2 ¶6(2)',
      },
      // Government Benefits
      {
        id: 'INC8',
        q: 'Which specific government benefits does the debtor or family unit receive? (CCB, GST/HST credit, OAS, GIS, disability, workers\' comp, provincial benefits)',
        why: 'Each benefit type has different reporting requirements. Some may be exempt from the operation of the Act. Details are required for government benefits, other benefits, and other income.',
        ref: 'Form 65 Guidance — Net government benefits, Other net income',
      },
      {
        id: 'INC9',
        q: 'Has the debtor provided a CRA Notice of Assessment, benefit statements, or MyAccount printout?',
        why: 'CRA documents independently verify declared income sources and amounts. The NOA shows total income, benefits received, and amounts owing.',
        ref: 'Professional practice — income verification',
      },
      {
        id: 'INC10',
        q: 'Is any income exempt from the operation of the Act? If so, what is the legislative basis?',
        why: 'Exempt income must be flagged and excluded from all surplus calculations. The enabling legislation must state the income is "not subject to the operation of any laws relating to bankruptcy or insolvency."',
        ref: 'Form 65 Guidance — Income exempt from the operation of the Act',
      },
      // Other / Unusual
      {
        id: 'INC11',
        q: 'Does the debtor receive any regular financial support from family members, friends, or other informal sources?',
        why: 'Regular cash gifts or support may constitute income. One-time gifts and inheritances are excluded from income under 11R2 but may be assets of the estate.',
        ref: 'Form 65 Guidance — Monthly Income Table note',
      },
      {
        id: 'INC12',
        q: 'Has the debtor received any lump sums this period — tax refund, insurance payout, settlement, severance, pay equity, wrongful dismissal damages?',
        why: 'Damages for wrongful dismissal, pay equity settlements, and workers\' comp are explicitly included as income. Severance and settlements may need to be averaged.',
        ref: 'Form 65 Guidance — Monthly Income Table',
      },
      {
        id: 'INC13',
        q: 'Does the debtor have income from rental property, investments, dividends, or any passive source?',
        why: 'Passive income is often unreported. Rental income from a property that is an asset of the estate is particularly relevant.',
        ref: 'BIA s.68(2)',
      },
    ],
  },

  deductions: {
    title: 'Non-Discretionary Payments Verification',
    ref: 'Directive 11R2-2025 ¶5(3), Form 65 Guidance — Monthly Non-Discretionary Payments',
    questions: [
      {
        id: 'DED1',
        q: 'If child or spousal support is paid: is there a court order or written separation agreement? Does the amount match?',
        why: 'Support payments are non-discretionary only when arising from a judicial decision or agreement. Informal arrangements without documentation cannot be verified.',
        ref: 'Form 65 Guidance — Child support / Spousal support',
      },
      {
        id: 'DED2',
        q: 'If child care is claimed: who provides the care? Is the debtor or family member employed or in school, making child care necessary?',
        why: 'Child care is non-discretionary only when it enables work, business operation, or school attendance. The amount must be net of any subsidies or tax benefits.',
        ref: 'Form 65 Guidance — Child care',
      },
      {
        id: 'DED3',
        q: 'If medical expenses are claimed: what is the clinically-recognized condition? Has a physician or specialist documented it?',
        why: 'The LIT must determine that expenses are "both non-discretionary and required due to a clinically-recognized condition, illness, disability or treatment." Net of insurance.',
        ref: 'Form 65 Guidance — Medical expenses',
      },
      {
        id: 'DED4',
        q: 'If employment/business expenses are claimed: what specifically are they? Are they required as a condition of employment?',
        why: 'Only expenses permitted by the Income Tax Act as a condition of employment qualify. Must not double-count expenses already deducted from self-employment net income.',
        ref: 'Form 65 Guidance — Expenses as a condition of employment',
      },
      {
        id: 'DED5',
        q: 'Are any deductions claimed here actually voluntary? (RRSP contributions, optional benefits, voluntary savings, union social fund)',
        why: 'Only mandatory statutory remittances and non-discretionary payments reduce income for surplus purposes. Voluntary deductions inflate the reduction and reduce surplus obligation.',
        ref: '11R2 ¶5(2)(a) — minimum statutory remittances',
      },
      {
        id: 'DED6',
        q: 'If the debtor is a salaried employee: do the income tax, CPP, and EI deductions on the pay stub match what is reported here?',
        why: 'Pay stubs independently verify statutory deductions. Discrepancies suggest either data entry error or deliberate inflation.',
        ref: '11R2 ¶5(2)(a)',
      },
      {
        id: 'DED7',
        q: 'Has a court lifted the stay of proceedings for any creditor? If so, what debt, what court order, and what payments are required?',
        why: 'Payments on debts where the stay has been lifted are a specific non-discretionary category. The court order must be on file.',
        ref: 'Form 65 Guidance — Debts where the stay has been lifted',
      },
    ],
  },

  expenses: {
    title: 'Living Expense Verification',
    ref: 'Form 65 Guidance — Monthly Expenses of the Family Unit Table',
    questions: [
      // Housing
      {
        id: 'EXP1',
        q: 'Has the debtor provided a lease agreement, rental receipt, or mortgage statement?',
        why: 'Housing is typically the largest expense. Documentary evidence establishes the actual obligation versus what the debtor reports.',
        ref: 'Form 65 Guidance — Housing: Rent/mortgage/hypothec',
      },
      {
        id: 'EXP2',
        q: 'Does the rent or lease include any bundled services (utilities, internet, parking, snow removal)?',
        why: 'If the lease includes other services, the full amount is reported under Rent. This prevents double-counting if utilities are also listed separately.',
        ref: 'Form 65 Guidance — Rent/mortgage/hypothec note',
      },
      {
        id: 'EXP3',
        q: 'Are roommates, family members, or others sharing housing costs? Is the reported amount the debtor\'s portion only?',
        why: 'Unreported cost-sharing inflates the debtor\'s apparent expenses. If two people split $1,400 rent, the household expense is $1,400 but the debtor may only be paying $700.',
        ref: 'Professional practice — expense verification',
      },
      {
        id: 'EXP4',
        q: 'Is there a secondary residence (cottage, seasonal property, shared ownership)?',
        why: 'Form 65 explicitly requires reporting expenses for both primary and secondary residences. A secondary residence may also be an asset of the estate.',
        ref: 'Form 65 Guidance — Housing subtypes (multiple references)',
      },
      // Food & Living
      {
        id: 'EXP5',
        q: 'Does the food amount cover the entire family unit? Are there special dietary needs (medical, religious, allergies) that affect costs?',
        why: 'Food is self-reported and difficult to verify. Context for the amount — family size, dietary requirements — helps assess reasonableness.',
        ref: 'Form 65 Guidance — Living: Food/grocery',
      },
      {
        id: 'EXP6',
        q: 'Is the debtor eating out regularly? Are restaurant meals reported under Food/Grocery or under Personal: Meals/Restaurants?',
        why: 'The OSB Form 65 separates groceries (Living category) from restaurant meals (Personal category). Combining them obscures discretionary spending.',
        ref: 'Form 65 Guidance — Personal: Meals/restaurants vs Living: Food/grocery',
      },
      // Transportation
      {
        id: 'EXP7',
        q: 'Does the debtor own, lease, or finance a vehicle? Provide documentation (lease/loan statement, registration).',
        why: 'Vehicle ownership, lease, and financing are reported differently. The vehicle itself may be an asset of the estate subject to Directive 25R.',
        ref: 'Form 65 Guidance — Transportation subtypes',
      },
      {
        id: 'EXP8',
        q: 'Is the vehicle necessary for employment or family needs? Is public transit a viable alternative?',
        why: 'Vehicle expenses are among the highest discretionary costs. Whether the vehicle is necessary affects whether the trustee may sell it back to the debtor.',
        ref: 'Directive 25R — Realization of Estate Assets',
      },
      {
        id: 'EXP9',
        q: 'How many vehicles does the family unit operate? Are all reported expenses accounted for across all vehicles?',
        why: 'Multiple vehicles multiply insurance, maintenance, and fuel costs. Each vehicle\'s expenses should be itemized.',
        ref: 'Form 65 Guidance — Transportation',
      },
      // Personal / Discretionary
      {
        id: 'EXP10',
        q: 'Does the debtor use tobacco, vaping products, cannabis, or alcohol? What is the honest monthly amount?',
        why: 'These have their own OSB categories. They are the most commonly underreported personal expenses. The form separates tobacco/vaping/cannabis from alcohol.',
        ref: 'Form 65 Guidance — Personal: Tobacco/vaping/cannabis, Alcohol',
      },
      {
        id: 'EXP11',
        q: 'Does the debtor gamble, hold gym/sports memberships, or have recurring entertainment subscriptions?',
        why: 'Gambling is specifically listed in the OSB Entertainment/Sports category. Ongoing subscriptions and memberships represent committed spending.',
        ref: 'Form 65 Guidance — Personal: Entertainment/sports',
      },
      {
        id: 'EXP12',
        q: 'Is the debtor making gifts or charitable donations during the bankruptcy period?',
        why: 'Gifts and donations to non-relatives during bankruptcy are unusual and potentially problematic when the debtor has surplus income obligations.',
        ref: 'Form 65 Guidance — Personal: Gifts/charitable donations',
      },
      // Insurance
      {
        id: 'EXP13',
        q: 'What insurance does the debtor carry? Distinguish mandatory (home, vehicle) from optional (life, contents, mortgage insurance).',
        why: 'Optional mortgage insurance is reported under Insurance—Home, not under Housing. Life insurance may have cash surrender value that is an estate asset.',
        ref: 'Form 65 Guidance — Insurance subtypes',
      },
      // Payments
      {
        id: 'EXP14',
        q: 'Is the debtor paying or committed to pay any financial advice provider other than the LIT? Who, how much, and for what?',
        why: 'This is a NEW Form 65 category (2024). Must include future committed amounts. Money for services already received is a provable claim — the LIT must verify no preference is being given.',
        ref: 'Form 65 Guidance — Payments: To provider of financial advice',
      },
      {
        id: 'EXP15',
        q: 'Is the debtor enrolled in a debt management plan from before filing? Are creditor payments and advisor fees separated?',
        why: 'If a debt management plan exists, only the professional fee goes under Financial Advice Provider. Creditor payments go under Payments—Other.',
        ref: 'Form 65 Guidance — Payments: To provider of financial advice notes',
      },
      // Overall
      {
        id: 'EXP16',
        q: 'Do total expenses exceed available income? If so, how is the debtor funding the deficit?',
        why: 'A persistent deficit implies unreported income, family support, or accumulating new debt — all of which affect the integrity of the Form 65.',
        ref: '11R2 ¶5 — available monthly income calculation',
      },
    ],
  },
};


// ========== INITIAL STATE ==========

const probeInit = () => {
  const p = {};
  Object.entries(PROBES).forEach(([section, data]) => {
    data.questions.forEach(q => {
      p[q.id] = { response: '', tag: '' };
    });
  });
  return p;
};

const init = {
  e: { tn:'', tf:'', tl:'', en:'', dn:'', fd:'', rm:'', fs:'', fd2:'', ft: true },
  i: Object.fromEntries(INC.map(c => [c.k, ''])),
  d: Object.fromEntries(DED.map(c => [c.k, ''])),
  x: Object.fromEntries(EXP.map(c => [c.k, ''])),
  n: { inc:'', exp:'', gen:'', chg:'' },
  probes: probeInit(),
};


// ========== DEMO CASE ==========

const demoProbes = (() => {
  const p = probeInit();
  // Family Unit
  p.FU1 = { response: 'Two people: debtor (Sarah Chen, age 34) and spouse (David Chen, age 36). No children. No other household members.', tag: 'VRB' };
  p.FU2 = { response: 'No. Only debtor and spouse contribute. No roommates or other contributors.', tag: 'VRB' };
  p.FU3 = { response: 'Yes, spouse David is cooperating. Provided pay stubs from part-time retail position at Canadian Tire.', tag: 'DOC' };
  p.FU4 = { response: 'N/A — no children.', tag: 'NA' };
  p.FU5 = { response: 'No changes since filing in November 2025.', tag: 'VRB' };
  p.FU6 = { response: 'N/A — spouse is cooperating fully.', tag: 'NA' };
  p.FU7 = { response: 'No. Both debtor and spouse work locally.', tag: 'VRB' };
  // Income
  p.INC1 = { response: 'Yes. Debtor provided 2 pay stubs from Metro Hospital (admin assistant), biweekly. Consistent with $3,200/mo net.', tag: 'DOC' };
  p.INC2 = { response: 'Debtor income is salary, consistent. No overtime or commission.', tag: 'DOC' };
  p.INC3 = { response: 'Debtor states no cash income, no side jobs. Spouse states same.', tag: 'VRB' };
  p.INC4 = { response: 'No job changes for either debtor or spouse since filing.', tag: 'VRB' };
  p.INC5 = { response: 'N/A — neither debtor nor spouse is self-employed.', tag: 'NA' };
  p.INC6 = { response: 'N/A.', tag: 'NA' };
  p.INC7 = { response: 'Yes. Spouse provided 2 Canadian Tire pay stubs. Part-time, approx 15 hrs/week at $18/hr. Net ~$1,080/mo.', tag: 'DOC' };
  p.INC8 = { response: 'Debtor receives GST/HST credit quarterly (~$125/quarter, not averaged into monthly). No other government benefits.', tag: 'VRB' };
  p.INC9 = { response: 'Debtor provided 2024 NOA showing total income $39,800. Consistent with current salary.', tag: 'DOC' };
  p.INC10 = { response: 'No exempt income identified.', tag: 'NA' };
  p.INC11 = { response: 'Debtor states parents occasionally help with groceries but not regularly. Estimated <$50/month.', tag: 'VRB' };
  p.INC12 = { response: 'No lump sums received this period.', tag: 'VRB' };
  p.INC13 = { response: 'No rental property, investments, or passive income.', tag: 'VRB' };
  // Deductions
  p.DED1 = { response: 'N/A — no child or spousal support obligations.', tag: 'NA' };
  p.DED2 = { response: 'N/A — no children, no child care.', tag: 'NA' };
  p.DED3 = { response: 'Medical expenses: debtor has prescription for asthma medication (Symbicort). $120/mo net of 80% employer coverage. Prescription on file.', tag: 'DOC' };
  p.DED4 = { response: 'N/A — no employment expenses claimed.', tag: 'NA' };
  p.DED5 = { response: 'Reviewed pay stubs. Only statutory deductions: income tax, CPP, EI, and mandatory group health ($45/mo employee share). No RRSP or voluntary items.', tag: 'DOC' };
  p.DED6 = { response: 'Pay stub deductions: Tax $520, CPP $215, EI $110, Group Health $45. All match reported amounts.', tag: 'DOC' };
  p.DED7 = { response: 'No stay of proceedings has been lifted for any creditor.', tag: 'NA' };
  // Expenses
  p.EXP1 = { response: 'Lease agreement provided. 1-bedroom apartment, $1,400/mo. Lease signed Sept 2024, renewed Sept 2025.', tag: 'DOC' };
  p.EXP2 = { response: 'Lease does NOT include utilities. Water is included in condo fees paid by landlord. All other utilities separate.', tag: 'DOC' };
  p.EXP3 = { response: 'No roommates. Debtor and spouse share all costs.', tag: 'VRB' };
  p.EXP4 = { response: 'No secondary residence.', tag: 'VRB' };
  p.EXP5 = { response: 'Food for two adults. Debtor has mild lactose intolerance, requires some specialty items. $480/mo reported.', tag: 'VRB' };
  p.EXP6 = { response: 'Debtor states eating out 1-2x per month, max $40. Included in food total, not separated.', tag: 'VRB' };
  p.EXP7 = { response: 'Debtor owns 2018 Honda Civic, no financing (paid off). Spouse does not drive.', tag: 'DOC' };
  p.EXP8 = { response: 'Vehicle used for commute to Metro Hospital (12 km each way). Public transit would add 45 min each way. Vehicle considered necessary for employment.', tag: 'VRB' };
  p.EXP9 = { response: 'One vehicle only. Insurance $145/mo, gas $95/mo, maintenance ~$45/mo averaged.', tag: 'DOC' };
  p.EXP10 = { response: 'Debtor does not smoke, vape, or use cannabis. Spouse states same. No alcohol expenses reported — debtor states they do not drink.', tag: 'VRB' };
  p.EXP11 = { response: 'No gym membership, no gambling. Netflix $16.99/mo included in cable/streaming.', tag: 'VRB' };
  p.EXP12 = { response: 'No gifts or donations during bankruptcy.', tag: 'VRB' };
  p.EXP13 = { response: 'Tenant insurance: $35/mo (mandatory per lease). No life insurance. Vehicle insurance: $145/mo (reported under transport).', tag: 'DOC' };
  p.EXP14 = { response: 'No payments to any financial advice provider other than the LIT.', tag: 'VRB' };
  p.EXP15 = { response: 'No debt management plan.', tag: 'NA' };
  p.EXP16 = { response: 'Available income $4,160 (net income $4,280 less $120 medical) vs total expenses $2,650 = household surplus $1,510. Expenses well within income. No deficit.', tag: 'VRB' };
  return p;
})();

const demo = {
  e: { tn:'J. Thompson, CIRP, LIT (DEMO)', tf:'Thompson & Associates Inc.', tl:'DEMO-LIT-4821', en:'EST-2026-0142', dn:'Sarah M. Chen', fd:'2025-11-15', rm:'2026-01', fs:'2', fd2:'Debtor and spouse (David Chen)', ft:true },
  i: { employment:'3200', spouseEmployment:'1080', selfEmploymentGross:'', selfEmployment:'', ei:'', pension:'', childSupport:'', spousalSupport:'', socialAssistance:'', otherInsurance:'', childBenefit:'', otherGovt:'', otherIncome:'' },
  d: { childSupportPaid:'', spousalSupportPaid:'', childCare:'', medical:'120', courtFines:'', employmentExpenses:'', stayLifted:'', otherNonDisc:'' },
  x: { rent:'1400', propTax:'', heating:'65', phone:'95', cable:'17', internet:'60', electricity:'40', water:'', furniture:'', housingOther:'',
       food:'480', laundry:'', grooming:'25', clothing:'40',
       vehiclePmt:'', vehicleIns:'145', vehicleMaint:'140', publicTransit:'',
       tobacco:'', alcohol:'', restaurants:'', entertainment:'', gifts:'', allowances:'',
       insHome:'', insContents:'35', insLife:'', 
       pmtEstate:'', pmtSecured:'', pmtFinAdvice:'', pmtOther:'' },
  n: { inc:'Spouse works part-time at Canadian Tire. No change from prior month. GST credit ~$125/quarter not averaged in.', exp:'Medical: asthma prescription net of 80% employer coverage. Food slightly higher due to dietary needs.', gen:'', chg:'Food increased $35 from December — dietary adjustment. All other categories stable.' },
  probes: demoProbes,
};


// ========== UTILITIES ==========
const p = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const sumI = (d) => INC.reduce((s, c) => c.k === 'selfEmploymentGross' ? s : s + p(d[c.k]), 0);
const sumD = (d) => DED.reduce((s, c) => s + p(d[c.k]), 0);
const sumX = (d) => EXP.reduce((s, c) => s + p(d[c.k]), 0);
const fmt = a => a < 0 ? `-$${Math.abs(a).toFixed(2)}` : `$${a.toFixed(2)}`;

function calcSurplus(ti, td, fs) {
  const net = ti - td;
  const sz = Math.min(Math.max(parseInt(fs)||1,1),7);
  const thr = LICO[sz].monthly;
  const surplus = net - thr;
  return { net, thr, sz, surplus, over200: surplus > 200, pay: surplus > 200 ? Math.round(surplus*50)/100 : 0 };
}

function checkGates(s) {
  const flags = [];
  const ti = sumI(s.i), td = sumD(s.d), tx = sumX(s.x);
  const fs = parseInt(s.e.fs)||1;
  const sr = calcSurplus(ti, td, s.e.fs);

  // Original consistency checks
  if (ti===0) flags.push({ sev:'fail', code:'CG1', msg:'Total income is $0', det:'Document the source of funds for living expenses.' });
  if (td>ti && ti>0) flags.push({ sev:'fail', code:'CG2', msg:`Non-discretionary payments (${fmt(td)}) exceed income (${fmt(ti)})`, det:'Net income cannot be negative. Review entries.' });
  if (p(s.x.rent)===0 && p(s.x.housingOther)===0) flags.push({ sev:'warn', code:'CG4', msg:'No housing expense reported', det:'If rent-free, document the arrangement in notes.' });
  const fpn = p(s.x.food)/fs;
  if (p(s.x.food)>0 && fpn>500) flags.push({ sev:'warn', code:'CG5a', msg:`Food is $${fpn.toFixed(0)}/person/month`, det:'Above typical ranges. Document justification.' });
  if (p(s.x.food)>0 && fpn<100 && fs<=3) flags.push({ sev:'warn', code:'CG5b', msg:`Food is $${fpn.toFixed(0)}/person/month`, det:'Below typical ranges. Verify all food costs reported.' });
  if (sr.net>0 && tx>sr.net*1.2) flags.push({ sev:'warn', code:'CG6', msg:`Expenses (${fmt(tx)}) exceed net income (${fmt(sr.net)})`, det:'Identify the funding source for the deficit.' });
  if (p(s.i.spouseEmployment)>0 && fs<2) flags.push({ sev:'fail', code:'CG7', msg:'Spouse income reported but family size is 1', det:'Update family unit size to at least 2.' });
  if (p(s.i.childBenefit)>0 && fs<2) flags.push({ sev:'warn', code:'CG8', msg:'CCB received but family size has no children', det:'CCB implies dependants. Verify family unit composition.' });
  if (!s.n.chg || s.n.chg.trim().length<5) flags.push({ sev:'info', code:'CG9', msg:'No changes from prior month documented', det:'Best practice: document changes or state "no changes."' });
  if (p(s.i.selfEmployment)>0 && p(s.i.selfEmploymentGross)===0) flags.push({ sev:'warn', code:'CG10', msg:'Net self-employment income reported without gross', det:'Form 65 requires both gross and net for self-employment.' });

  // NEW v2: Probe-enhanced checks
  const probeCount = Object.values(s.probes).filter(pr => pr.response.trim().length > 0).length;
  const totalProbes = Object.keys(s.probes).length;
  const unverifiedCount = Object.values(s.probes).filter(pr => pr.tag === 'UNV').length;
  const documentedCount = Object.values(s.probes).filter(pr => pr.tag === 'DOC').length;

  if (probeCount === 0) {
    flags.push({ sev:'info', code:'PG1', msg:'No probe questions have been answered', det:'Completing probe questions strengthens the file and creates an epistemic trail.' });
  } else if (probeCount < totalProbes * 0.3) {
    flags.push({ sev:'info', code:'PG2', msg:`Only ${probeCount} of ${totalProbes} probes completed (${Math.round(probeCount/totalProbes*100)}%)`, det:'Consider completing more probe questions for key sections.' });
  }
  if (unverifiedCount > 5) {
    flags.push({ sev:'warn', code:'PG3', msg:`${unverifiedCount} probe responses tagged as "Unverified"`, det:'High number of unverified items. Consider requesting supporting documentation.' });
  }
  if (ti > 0 && documentedCount === 0 && probeCount > 0) {
    flags.push({ sev:'warn', code:'PG4', msg:'No probe responses have documentary evidence', det:'Income verification typically requires at least pay stubs or benefit statements.' });
  }
  // Self-employment without documentation
  if (p(s.i.selfEmployment) > 0) {
    const se5 = s.probes.INC5;
    if (se5 && se5.tag === 'UNV') {
      flags.push({ sev:'warn', code:'PG5', msg:'Self-employment income has no documentary support', det:'Self-employment deductions require ITA documentation. Request business records.' });
    }
  }

  const fc=flags.filter(f=>f.sev==='fail').length, wc=flags.filter(f=>f.sev==='warn').length, ic=flags.filter(f=>f.sev==='info').length;
  return { status: fc>0?'fail':wc>0?'review':'pass', flags, fc, wc, ic };
}


// ========== FILE I/O: Save / Load / Export ==========

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function makeFilename(s, ext) {
  const estate = (s.e.en || 'DRAFT').replace(/[^a-zA-Z0-9-]/g, '');
  const month = (s.e.rm || 'NODATE').replace(/[^a-zA-Z0-9-]/g, '');
  return `Form65_${estate}_${month}.${ext}`;
}

// --- SAVE: Full state as JSON ---
function saveCase(s, dm) {
  const payload = {
    _format: 'NMA-BANKRUPTCY-FORM65',
    _version: '2.1',
    _savedAt: new Date().toISOString(),
    _isDemo: dm,
    estate: s.e,
    income: s.i,
    deductions: s.d,
    expenses: s.x,
    notes: s.n,
    probes: s.probes,
  };
  downloadFile(JSON.stringify(payload, null, 2), makeFilename(s, 'json'), 'application/json');
}

// --- LOAD: Read JSON back into state ---
function loadCase(file, setS, setDm, setScr) {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target.result);
      if (data._format !== 'NMA-BANKRUPTCY-FORM65') {
        alert('This file is not a valid NMA-Bankruptcy Form 65 case file.');
        return;
      }
      const loaded = {
        e: data.estate || init.e,
        i: data.income || init.i,
        d: data.deductions || init.d,
        x: data.expenses || init.x,
        n: data.notes || init.n,
        probes: data.probes || probeInit(),
      };
      setS(loaded);
      setDm(data._isDemo || false);
      setScr(0);
    } catch (err) {
      alert('Could not read file. Make sure it is a valid Form 65 JSON file.\n\n' + err.message);
    }
  };
  reader.readAsText(file);
}

// --- XML EXPORT: Structured to match OSB E-Filing Form 65 field layout ---
// Reference: Form 65 Guidance (2025-04-02), Directive 8R21/8R22
// This XML is NOT a direct E-Filing submission — it mirrors the field structure
// so trustees can map data into Ascend, Aryza, or other filing software.

const XML_INCOME_MAP = {
  employment: 'NetEmploymentIncome',
  spouseEmployment: 'NetEmploymentIncome_FamilyUnit',
  selfEmploymentGross: 'GrossSelfEmploymentIncome',
  selfEmployment: 'NetSelfEmploymentIncome',
  ei: 'NetEmploymentInsuranceBenefits',
  pension: 'NetPensionOrAnnuities',
  childSupport: 'NetChildSupport',
  spousalSupport: 'NetSpousalSupport',
  socialAssistance: 'NetSocialAssistance',
  otherInsurance: 'NetOtherInsuranceBenefits',
  childBenefit: 'NetGovernmentBenefits_CCB',
  otherGovt: 'NetGovernmentBenefits_Other',
  otherIncome: 'OtherNetIncome',
};

const XML_DED_MAP = {
  childSupportPaid: 'ChildSupport',
  spousalSupportPaid: 'SpousalSupport',
  childCare: 'ChildCare',
  medical: 'MedicalExpenses',
  courtFines: 'CourtImposedFinesOrPenalties',
  employmentExpenses: 'ExpensesAsConditionOfEmployment',
  stayLifted: 'DebtsStayLifted',
  otherNonDisc: 'OtherPayments',
};

const XML_EXP_MAP = {
  rent: { type: 'Housing', subtype: 'RentMortgageHypothec' },
  propTax: { type: 'Housing', subtype: 'PropertyTaxesCondoFees' },
  heating: { type: 'Housing', subtype: 'HeatingGasOil' },
  phone: { type: 'Housing', subtype: 'TelephoneCellPhone' },
  cable: { type: 'Housing', subtype: 'CableStreamingServices' },
  internet: { type: 'Housing', subtype: 'Internet' },
  electricity: { type: 'Housing', subtype: 'Electricity' },
  water: { type: 'Housing', subtype: 'Water' },
  furniture: { type: 'Housing', subtype: 'Furniture' },
  housingOther: { type: 'Housing', subtype: 'Other' },
  food: { type: 'Living', subtype: 'FoodGrocery' },
  laundry: { type: 'Living', subtype: 'LaundryDryCleaning' },
  grooming: { type: 'Living', subtype: 'GroomingToiletries' },
  clothing: { type: 'Living', subtype: 'Clothing' },
  vehiclePmt: { type: 'Transportation', subtype: 'VehicleLeasePayments' },
  vehicleIns: { type: 'Transportation', subtype: 'VehicleInsuranceRegistration' },
  vehicleMaint: { type: 'Transportation', subtype: 'RepairMaintenanceGas' },
  publicTransit: { type: 'Transportation', subtype: 'PublicTransportation' },
  tobacco: { type: 'Personal', subtype: 'TobaccoVapingCannabis' },
  alcohol: { type: 'Personal', subtype: 'Alcohol' },
  restaurants: { type: 'Personal', subtype: 'MealsRestaurants' },
  entertainment: { type: 'Personal', subtype: 'EntertainmentSports' },
  gifts: { type: 'Personal', subtype: 'GiftsCharitableDonations' },
  allowances: { type: 'Personal', subtype: 'Allowances' },
  insHome: { type: 'Insurance', subtype: 'Home' },
  insContents: { type: 'Insurance', subtype: 'FurnitureContents' },
  insLife: { type: 'Insurance', subtype: 'Life' },
  pmtEstate: { type: 'Payments', subtype: 'ToTheEstate' },
  pmtSecured: { type: 'Payments', subtype: 'ToSecuredCreditor' },
  pmtFinAdvice: { type: 'Payments', subtype: 'ToProviderOfFinancialAdvice' },
  pmtOther: { type: 'Payments', subtype: 'Other' },
};

function esc(str) { return String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

function exportXML(s) {
  const ti = sumI(s.i), td = sumD(s.d), tx = sumX(s.x);
  const sr = calcSurplus(ti, td, s.e.fs);
  const fs = parseInt(s.e.fs) || 1;
  const pct = sr.net > 0 ? Math.min(100, Math.round((ti / (ti + sumI(s.i) - ti || 1)) * 100)) : 100;
  // Build bankrupt's portion percentage
  const bankruptIncome = INC.reduce((sum, c) => c.k === 'selfEmploymentGross' || c.k === 'spouseEmployment' ? sum : sum + p(s.i[c.k]), 0);
  const totalFamilyIncome = ti;
  const bankruptPct = totalFamilyIncome > 0 ? Math.min(100, Math.round(bankruptIncome / totalFamilyIncome * 100)) : 100;

  let x = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  x += `<!-- NMA-Bankruptcy Form 65 Export v2.1 -->\n`;
  x += `<!-- Structured to mirror OSB E-Filing Form 65 field layout -->\n`;
  x += `<!-- Reference: Form 65 Guidance (2025-04-02), Directive 11R2-2025 -->\n`;
  x += `<!-- NOT a direct E-Filing submission. Use to map into Ascend/Aryza/ISS. -->\n`;
  x += `<Form65 exportDate="${new Date().toISOString()}" version="2.1">\n`;

  // Estate Info
  x += `  <EstateInformation>\n`;
  x += `    <TrusteeName>${esc(s.e.tn)}</TrusteeName>\n`;
  x += `    <TrusteeFirm>${esc(s.e.tf)}</TrusteeFirm>\n`;
  x += `    <TrusteeLicense>${esc(s.e.tl)}</TrusteeLicense>\n`;
  x += `    <EstateNumber>${esc(s.e.en)}</EstateNumber>\n`;
  x += `    <DebtorName>${esc(s.e.dn)}</DebtorName>\n`;
  x += `    <FilingDate>${esc(s.e.fd)}</FilingDate>\n`;
  x += `    <ReportingMonth>${esc(s.e.rm)}</ReportingMonth>\n`;
  x += `    <FamilyUnitSize>${fs}</FamilyUnitSize>\n`;
  x += `    <FamilyUnitDetails>${esc(s.e.fd2)}</FamilyUnitDetails>\n`;
  x += `    <FirstTimeBankruptcy>${s.e.ft ? 'true' : 'false'}</FirstTimeBankruptcy>\n`;
  x += `    <FilingStatus>Original</FilingStatus>\n`;
  x += `  </EstateInformation>\n`;

  // Monthly Income Table
  x += `  <MonthlyIncome>\n`;
  let incNo = 1;
  INC.forEach(c => {
    const val = p(s.i[c.k]);
    if (val > 0 || c.k === 'selfEmploymentGross') {
      if (val > 0) {
        const xmlName = XML_INCOME_MAP[c.k] || c.k;
        const isDebtor = c.k !== 'spouseEmployment';
        x += `    <IncomeSource no="${incNo}">\n`;
        x += `      <TypeOfIncome>${xmlName}</TypeOfIncome>\n`;
        x += `      <Description>${esc(c.l)}</Description>\n`;
        x += `      <Amount currency="CAD">${val.toFixed(2)}</Amount>\n`;
        x += `      <Member>${isDebtor ? 'Bankrupt' : 'FamilyUnit'}</Member>\n`;
        if (c.k === 'selfEmploymentGross') x += `      <ExcludeFromTotal>true</ExcludeFromTotal>\n`;
        x += `    </IncomeSource>\n`;
        incNo++;
      }
    }
  });
  x += `    <TotalNetMonthlyIncome_Line1>${bankruptIncome.toFixed(2)}</TotalNetMonthlyIncome_Line1>\n`;
  x += `    <TotalNetMonthlyIncome_Line2>${(ti - bankruptIncome).toFixed(2)}</TotalNetMonthlyIncome_Line2>\n`;
  x += `    <TotalNetMonthlyIncomeOfFamilyUnit_Line3>${ti.toFixed(2)}</TotalNetMonthlyIncomeOfFamilyUnit_Line3>\n`;
  x += `  </MonthlyIncome>\n`;

  // Non-Discretionary Payments
  x += `  <MonthlyNonDiscretionaryPayments>\n`;
  let dedNo = 1;
  DED.forEach(c => {
    const val = p(s.d[c.k]);
    if (val > 0) {
      x += `    <Payment no="${dedNo}">\n`;
      x += `      <TypeOfPayment>${XML_DED_MAP[c.k] || c.k}</TypeOfPayment>\n`;
      x += `      <Description>${esc(c.l)}</Description>\n`;
      x += `      <Amount currency="CAD">${val.toFixed(2)}</Amount>\n`;
      x += `    </Payment>\n`;
      dedNo++;
    }
  });
  x += `    <TotalMonthlyNonDiscretionaryPayments_Line4>${td.toFixed(2)}</TotalMonthlyNonDiscretionaryPayments_Line4>\n`;
  x += `    <TotalMonthlyNonDiscretionaryPayments_Line5>0.00</TotalMonthlyNonDiscretionaryPayments_Line5>\n`;
  x += `    <TotalMonthlyNonDiscretionaryPaymentsOfFamilyUnit_Line6>${td.toFixed(2)}</TotalMonthlyNonDiscretionaryPaymentsOfFamilyUnit_Line6>\n`;
  x += `  </MonthlyNonDiscretionaryPayments>\n`;

  // Available Monthly Income
  x += `  <AvailableMonthlyIncome>\n`;
  x += `    <AvailableMonthlyIncome_Bankrupt_Line7>${(bankruptIncome - td).toFixed(2)}</AvailableMonthlyIncome_Bankrupt_Line7>\n`;
  x += `    <AvailableMonthlyIncomeOfFamilyUnit_Line8>${sr.net.toFixed(2)}</AvailableMonthlyIncomeOfFamilyUnit_Line8>\n`;
  x += `    <BankruptPortionPercentage_Line9>${bankruptPct}</BankruptPortionPercentage_Line9>\n`;
  x += `  </AvailableMonthlyIncome>\n`;

  // Monthly Expenses
  x += `  <MonthlyExpensesOfFamilyUnit>\n`;
  let expNo = 1;
  EXP.forEach(c => {
    const val = p(s.x[c.k]);
    if (val > 0) {
      const m = XML_EXP_MAP[c.k] || { type: 'Other', subtype: c.k };
      x += `    <Expense no="${expNo}">\n`;
      x += `      <TypeOfExpense>${m.type}</TypeOfExpense>\n`;
      x += `      <Subtype>${m.subtype}</Subtype>\n`;
      x += `      <Description>${esc(c.l)}</Description>\n`;
      x += `      <MonthlyTotal currency="CAD">${val.toFixed(2)}</MonthlyTotal>\n`;
      x += `    </Expense>\n`;
      expNo++;
    }
  });
  x += `    <TotalMonthlyExpensesOfFamilyUnit_Line10>${tx.toFixed(2)}</TotalMonthlyExpensesOfFamilyUnit_Line10>\n`;
  x += `    <MonthlySurplusDeficitOfFamilyUnit_Line11>${(sr.net - tx).toFixed(2)}</MonthlySurplusDeficitOfFamilyUnit_Line11>\n`;
  x += `  </MonthlyExpensesOfFamilyUnit>\n`;

  // Surplus Income Calculation
  x += `  <SurplusIncomeCalculation directive="11R2-2025">\n`;
  x += `    <NumberOfPersonsInFamilyUnit>${fs}</NumberOfPersonsInFamilyUnit>\n`;
  x += `    <SuperintendentStandard>${sr.thr.toFixed(2)}</SuperintendentStandard>\n`;
  x += `    <SurplusIncome>${Math.max(0, sr.surplus).toFixed(2)}</SurplusIncome>\n`;
  x += `    <SurplusExceeds200>${sr.over200}</SurplusExceeds200>\n`;
  x += `    <MonthlyAmountRequired_Line15>${sr.pay.toFixed(2)}</MonthlyAmountRequired_Line15>\n`;
  x += `  </SurplusIncomeCalculation>\n`;

  // Notes
  x += `  <Remarks>\n`;
  if (s.n.inc) x += `    <IncomeNotes>${esc(s.n.inc)}</IncomeNotes>\n`;
  if (s.n.exp) x += `    <ExpenseNotes>${esc(s.n.exp)}</ExpenseNotes>\n`;
  if (s.n.chg) x += `    <ChangesFromPriorMonth>${esc(s.n.chg)}</ChangesFromPriorMonth>\n`;
  if (s.n.gen) x += `    <OtherApplicableComments>${esc(s.n.gen)}</OtherApplicableComments>\n`;
  x += `  </Remarks>\n`;

  x += `</Form65>\n`;

  downloadFile(x, makeFilename(s, 'xml'), 'application/xml');
}


// --- CSV EXPORT ---
function exportCSV(s) {
  const ti = sumI(s.i), td = sumD(s.d), tx = sumX(s.x);
  const sr = calcSurplus(ti, td, s.e.fs);
  const fs = parseInt(s.e.fs) || 1;

  let rows = [];
  rows.push(['NMA-Bankruptcy Form 65 Export', '', '']);
  rows.push(['Export Date', new Date().toISOString(), '']);
  rows.push(['', '', '']);
  rows.push(['ESTATE INFORMATION', '', '']);
  rows.push(['Trustee', s.e.tn, '']);
  rows.push(['Firm', s.e.tf, '']);
  rows.push(['License', s.e.tl, '']);
  rows.push(['Estate Number', s.e.en, '']);
  rows.push(['Debtor Name', s.e.dn, '']);
  rows.push(['Filing Date', s.e.fd, '']);
  rows.push(['Reporting Month', s.e.rm, '']);
  rows.push(['Family Unit Size', fs, '']);
  rows.push(['Family Unit Details', s.e.fd2, '']);
  rows.push(['First Time Bankruptcy', s.e.ft ? 'Yes' : 'No', '']);
  rows.push(['', '', '']);

  rows.push(['MONTHLY INCOME', 'Amount', 'OSB Type']);
  INC.forEach(c => { if (p(s.i[c.k]) > 0) rows.push([c.l, p(s.i[c.k]).toFixed(2), XML_INCOME_MAP[c.k] || '']); });
  rows.push(['TOTAL NET MONTHLY INCOME', ti.toFixed(2), 'Line 3']);
  rows.push(['', '', '']);

  rows.push(['NON-DISCRETIONARY PAYMENTS', 'Amount', 'OSB Type']);
  DED.forEach(c => { if (p(s.d[c.k]) > 0) rows.push([c.l, p(s.d[c.k]).toFixed(2), XML_DED_MAP[c.k] || '']); });
  rows.push(['TOTAL NON-DISCRETIONARY', td.toFixed(2), 'Line 6']);
  rows.push(['', '', '']);

  rows.push(['AVAILABLE MONTHLY INCOME', sr.net.toFixed(2), 'Line 8']);
  rows.push(['', '', '']);

  rows.push(['MONTHLY EXPENSES', 'Amount', 'OSB Type/Subtype']);
  EXP.forEach(c => { if (p(s.x[c.k]) > 0) { const m = XML_EXP_MAP[c.k]; rows.push([c.l, p(s.x[c.k]).toFixed(2), m ? `${m.type}/${m.subtype}` : '']); }});
  rows.push(['TOTAL MONTHLY EXPENSES', tx.toFixed(2), 'Line 10']);
  rows.push(['SURPLUS (DEFICIT) OF FAMILY UNIT', (sr.net - tx).toFixed(2), 'Line 11']);
  rows.push(['', '', '']);

  rows.push(['SURPLUS INCOME CALCULATION', '', '']);
  rows.push(['Superintendent Standard ('+LICO[sr.sz].label+')', sr.thr.toFixed(2), 'Appendix A']);
  rows.push(['Surplus / (Deficit)', sr.surplus.toFixed(2), '']);
  rows.push(['Surplus Exceeds $200', sr.over200 ? 'Yes' : 'No', '']);
  rows.push(['Monthly Payment Required (Line 15)', sr.pay.toFixed(2), '50% of surplus']);
  rows.push(['', '', '']);

  rows.push(['NOTES', '', '']);
  if (s.n.inc) rows.push(['Income Notes', s.n.inc, '']);
  if (s.n.exp) rows.push(['Expense Notes', s.n.exp, '']);
  if (s.n.chg) rows.push(['Changes', s.n.chg, '']);
  if (s.n.gen) rows.push(['General', s.n.gen, '']);

  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  downloadFile(csv, makeFilename(s, 'csv'), 'text/csv');
}


// ========== STYLES ==========
const S = {
  ink:'#0c1820', slate:'#1a2836', text:'#556a7b', muted:'#8899a6', 
  accent:'#1b6b93', accentDk:'#145474', accentLt:'#e8f4fa',
  success:'#0a8754', successBg:'#ecfdf5', 
  warn:'#c67d15', warnBg:'#fffbeb', 
  danger:'#c4382a', dangerBg:'#fef2f2',
  bg:'#f8f9fb', surface:'#fff', border:'#dde3e8', borderLt:'#edf0f3', infoBg:'#eff6ff',
  probe:'#f6f0ff', probeBorder:'#d8c9f0', probeAccent:'#6b4c9a', probeMuted:'#8b72b0',
};
const F = "'DM Sans', sans-serif";
const FM = "'IBM Plex Mono', monospace";


// ========== PROBE PANEL COMPONENT ==========

function ProbePanel({ section, probes, updateProbe }) {
  const [open, setOpen] = useState(false);
  const data = PROBES[section];
  if (!data) return null;

  const answered = data.questions.filter(q => probes[q.id]?.response?.trim()).length;
  const total = data.questions.length;
  const docCount = data.questions.filter(q => probes[q.id]?.tag === 'DOC').length;
  const vrbCount = data.questions.filter(q => probes[q.id]?.tag === 'VRB').length;
  const unvCount = data.questions.filter(q => probes[q.id]?.tag === 'UNV').length;

  const tagColors = {
    DOC: { bg: '#ecfdf5', border: '#a7f3d0', text: '#0a8754', label: 'Documented' },
    VRB: { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', label: 'Verbal' },
    UNV: { bg: '#fef2f2', border: '#fecaca', text: '#c4382a', label: 'Unverified' },
    NA:  { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280', label: 'N/A' },
  };

  return (
    <div style={{ 
      border: `1.5px solid ${open ? S.probeBorder : S.borderLt}`, 
      borderRadius: 10, 
      marginBottom: 16, 
      background: open ? S.probe : '#faf8ff',
      transition: 'all 0.2s ease',
    }}>
      {/* Header */}
      <button 
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '14px 18px', border: 'none', background: 'transparent',
          cursor: 'pointer', fontFamily: F, textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ 
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 6,
            background: answered === total && total > 0 ? S.successBg : 'rgba(107,76,154,0.1)',
            border: `1.5px solid ${answered === total && total > 0 ? '#a7f3d0' : 'rgba(107,76,154,0.2)'}`,
            fontSize: 13, fontWeight: 700,
            color: answered === total && total > 0 ? S.success : S.probeAccent,
          }}>
            {answered === total && total > 0 ? '✓' : '?'}
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: S.probeAccent }}>
              {data.title}
            </div>
            <div style={{ fontSize: 11, color: S.probeMuted, marginTop: 1 }}>
              {answered}/{total} answered
              {answered > 0 && ` · ${docCount} documented · ${vrbCount} verbal · ${unvCount} unverified`}
            </div>
          </div>
        </div>
        <span style={{ fontSize: 18, color: S.probeAccent, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
      </button>

      {/* Questions */}
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <div style={{ 
            padding: '10px 14px', background: 'rgba(107,76,154,0.06)', 
            borderRadius: 6, marginBottom: 16, fontSize: 12, color: S.probeMuted, lineHeight: 1.5,
            borderLeft: `3px solid ${S.probeAccent}`,
          }}>
            <strong style={{ color: S.probeAccent }}>Reference:</strong> {data.ref}<br/>
            <strong style={{ color: S.probeAccent }}>Purpose:</strong> These questions create an epistemic trail. Responses are <strong>internal only</strong> — they do not appear on the printed Form 65.
          </div>

          {data.questions.map((q, idx) => {
            const probe = probes[q.id] || { response: '', tag: '' };
            return (
              <div key={q.id} style={{ 
                marginBottom: 14, padding: 14, background: S.surface, 
                borderRadius: 8, border: `1px solid ${S.borderLt}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S.slate, lineHeight: 1.45, flex: 1, fontFamily: F }}>
                    <span style={{ 
                      display: 'inline-block', fontSize: 10, fontWeight: 700, fontFamily: FM,
                      color: S.probeAccent, background: 'rgba(107,76,154,0.08)', 
                      padding: '2px 6px', borderRadius: 3, marginRight: 6, verticalAlign: 'middle',
                      letterSpacing: '0.05em',
                    }}>{q.id}</span>
                    {q.q}
                  </div>
                </div>
                
                <div style={{ fontSize: 11, color: S.probeMuted, marginBottom: 8, lineHeight: 1.4, fontStyle: 'italic', fontFamily: F }}>
                  <strong>Why this matters:</strong> {q.why}
                </div>

                <textarea
                  style={{
                    width: '100%', padding: '8px 10px', border: `1.5px solid ${S.border}`,
                    borderRadius: 6, fontSize: 13, fontFamily: F, color: S.slate,
                    minHeight: 54, resize: 'vertical', boxSizing: 'border-box',
                  }}
                  value={probe.response}
                  onChange={e => updateProbe(q.id, 'response', e.target.value)}
                  placeholder="Record debtor's response and any observations..."
                />

                {/* Epistemic Tag */}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: S.muted, lineHeight: '28px', fontFamily: F, marginRight: 4 }}>Evidence:</span>
                  {Object.entries(tagColors).map(([tag, style]) => (
                    <button
                      key={tag}
                      onClick={() => updateProbe(q.id, 'tag', probe.tag === tag ? '' : tag)}
                      style={{
                        padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        fontFamily: F, cursor: 'pointer', transition: 'all 0.15s',
                        border: `1.5px solid ${probe.tag === tag ? style.border : '#e5e7eb'}`,
                        background: probe.tag === tag ? style.bg : 'transparent',
                        color: probe.tag === tag ? style.text : S.muted,
                      }}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>

                {q.ref && (
                  <div style={{ fontSize: 10, color: '#b0a0c0', marginTop: 6, fontFamily: FM }}>
                    Ref: {q.ref}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ========== MAIN APP ==========

export default function Form65Navigator() {
  const [scr, setScr] = useState(0);
  const [s, setS] = useState(init);
  const [dm, setDm] = useState(false);

  const ue = (f,v) => setS(p=>({...p,e:{...p.e,[f]:v}}));
  const ui = (f,v) => setS(p=>({...p,i:{...p.i,[f]:v}}));
  const ud = (f,v) => setS(p=>({...p,d:{...p.d,[f]:v}}));
  const ux = (f,v) => setS(p=>({...p,x:{...p.x,[f]:v}}));
  const un = (f,v) => setS(p=>({...p,n:{...p.n,[f]:v}}));
  const updateProbe = (id, field, value) => setS(prev => ({
    ...prev, probes: { ...prev.probes, [id]: { ...prev.probes[id], [field]: value } }
  }));

  const ti = sumI(s.i), td = sumD(s.d), tx = sumX(s.x);
  const sr = calcSurplus(ti, td, s.e.fs);
  const cg = checkGates(s);
  const scrNames = ['Estate Info','Income','Non-Disc. Payments','Expenses','Notes','Consistency','Report'];
  const next = () => setScr(p => Math.min(p+1, 6));
  const prev = () => setScr(p => Math.max(p-1, 0));

  // Shared styles
  const card = { background:S.surface, border:`1px solid ${S.border}`, borderRadius:12, padding:24, marginBottom:16 };
  const label = { display:'block', marginBottom:3, fontSize:13, fontWeight:600, color:S.text, fontFamily:F };
  const input = { width:'100%', padding:'10px 12px', border:`1.5px solid ${S.border}`, borderRadius:8, fontSize:14, fontFamily:F, color:S.slate, boxSizing:'border-box' };
  const mono = { ...input, fontFamily:FM, fontWeight:600, textAlign:'right' };
  const btn = (bg,c,b) => ({ padding:'10px 20px', borderRadius:8, border:b||'none', background:bg, color:c, fontSize:14, fontWeight:600, fontFamily:F, cursor:'pointer' });
  const sect = { marginTop:24, paddingTop:20, borderTop:`1px solid ${S.borderLt}` };
  const sectTitle = { fontSize:14, fontWeight:700, color:S.slate, margin:'0 0 16px', paddingBottom:4, borderBottom:`2px solid ${S.accent}`, display:'inline-block', fontFamily:F };
  const directive = { padding:'12px 16px', background:'#f8f5f0', borderLeft:`3px solid ${S.warn}`, borderRadius:'0 4px 4px 0', marginBottom:16, fontSize:13, color:S.text, lineHeight:1.5, fontFamily:F };
  const info = { padding:'12px 16px', background:S.accentLt, border:`1px solid rgba(27,107,147,0.2)`, borderRadius:8, marginBottom:16, fontSize:13, color:S.accentDk, lineHeight:1.5, fontFamily:F };
  const lineItem = { display:'grid', gridTemplateColumns:'1fr 140px', gap:12, alignItems:'center', padding:'8px 0', borderBottom:`1px solid ${S.borderLt}` };
  const totalLine = { display:'grid', gridTemplateColumns:'1fr 140px', gap:12, alignItems:'center', padding:'12px 0', borderTop:`2px solid ${S.ink}`, marginTop:8 };

  const LineInput = ({ cats, data, update }) => {
    let currentCat = '';
    return cats.map(c => {
      const catHeader = c.cat && c.cat !== currentCat;
      if (c.cat) currentCat = c.cat;
      return (
        <React.Fragment key={c.k}>
          {catHeader && (
            <div style={{ 
              fontSize: 11, fontWeight: 700, color: S.probeAccent, textTransform: 'uppercase',
              letterSpacing: '0.08em', padding: '12px 0 4px', fontFamily: F,
              borderBottom: `1px solid ${S.probeBorder}`, marginTop: 8,
            }}>
              {c.cat}
            </div>
          )}
          <div style={lineItem}>
            <div>
              <div style={{ fontSize:14, color:S.text, fontFamily:F }}>{c.l}</div>
              <div style={{ fontSize:11, color:S.muted, marginTop:1 }}>{c.h}</div>
            </div>
            <input type="number" style={mono} value={data[c.k]} onChange={e=>update(c.k,e.target.value)} placeholder="0.00" min="0" step="0.01" />
          </div>
        </React.Fragment>
      );
    });
  };


  // ===== SCREEN 0: Estate Info =====
  const Screen0 = () => (
    <div style={card}>
      <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:S.ink, fontFamily:F }}>Estate Information</h2>
      <p style={{ margin:'4px 0 20px', fontSize:14, color:S.muted, fontFamily:F }}>Trustee, debtor, and estate details for this reporting period</p>

      {!dm && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, padding:16, background:S.accentLt, border:`1.5px solid rgba(27,107,147,0.25)`, borderRadius:8, marginBottom:24 }}>
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <span style={{ fontSize:24 }}>📋</span>
            <div><strong style={{ fontFamily:F }}>New to the Form 65 Navigator?</strong><p style={{ margin:'3px 0 0', fontSize:13, color:S.muted, fontFamily:F }}>Try the demo to see guided form completion with epistemic probes.</p></div>
          </div>
          <button style={btn(S.accentLt, S.accentDk, `1.5px solid ${S.accent}`)} onClick={()=>{setS(demo);setDm(true);}}>Load Demo →</button>
        </div>
      )}

      {/* Save / Load Case */}
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        <button style={btn('#f0edf5', S.probeAccent, `1.5px solid ${S.probeBorder}`)} onClick={() => { const inp = document.createElement('input'); inp.type='file'; inp.accept='.json'; inp.onchange=(e)=>{ if(e.target.files[0]) loadCase(e.target.files[0], setS, setDm, setScr); }; inp.click(); }}>
          📂 Load Case File
        </button>
        {(s.e.en || s.e.dn) && (
          <button style={btn('#f0edf5', S.probeAccent, `1.5px solid ${S.probeBorder}`)} onClick={() => saveCase(s, dm)}>
            💾 Save Case File
          </button>
        )}
      </div>

      <div style={sect}><div style={sectTitle}>Trustee</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div><div style={label}>Trustee Name *</div><input style={input} value={s.e.tn} onChange={e=>ue('tn',e.target.value)} placeholder="Full name and designation" /></div>
          <div><div style={label}>License No. *</div><input style={input} value={s.e.tl} onChange={e=>ue('tl',e.target.value)} placeholder="OSB license number" /></div>
        </div>
        <div style={{ marginTop:12 }}><div style={label}>Firm *</div><input style={input} value={s.e.tf} onChange={e=>ue('tf',e.target.value)} placeholder="Firm name" /></div>
      </div>

      <div style={sect}><div style={sectTitle}>Estate</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div><div style={label}>Estate Number *</div><input style={input} value={s.e.en} onChange={e=>ue('en',e.target.value)} placeholder="Estate/file number" /></div>
          <div><div style={label}>Debtor Name *</div><input style={input} value={s.e.dn} onChange={e=>ue('dn',e.target.value)} placeholder="Full legal name" /></div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginTop:12 }}>
          <div><div style={label}>Filing Date *</div><input type="date" style={input} value={s.e.fd} onChange={e=>ue('fd',e.target.value)} /></div>
          <div><div style={label}>Reporting Month *</div><input type="month" style={input} value={s.e.rm} onChange={e=>ue('rm',e.target.value)} /></div>
        </div>
      </div>

      <div style={sect}><div style={sectTitle}>Family Unit</div>
        <div style={directive}><strong>Directive 11R2-2025 ¶4:</strong> The family unit includes persons who reside in the same household and benefit from either the expenses incurred or the income earned by the debtor, or who contribute to the expenses or earnings — including persons not residing in the household (shared custody, rotational workers).</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div><div style={label}>Family Unit Size *</div>
            <select style={input} value={s.e.fs} onChange={e=>ue('fs',e.target.value)}>
              <option value="">Select...</option>
              {Object.entries(LICO).map(([sz,i])=><option key={sz} value={sz}>{i.label} — ${i.monthly.toLocaleString()}/mo</option>)}
            </select>
          </div>
          <div><div style={label}>First-Time Bankruptcy?</div>
            <div style={{ display:'flex', gap:8 }}>
              {[true,false].map(v=>(
                <button key={String(v)} onClick={()=>ue('ft',v)} style={{ flex:1, padding:'10px', borderRadius:8, border:`1.5px solid ${s.e.ft===v?S.accent:S.border}`, background:s.e.ft===v?S.accentLt:'transparent', color:s.e.ft===v?S.accentDk:S.text, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:F }}>{v?'Yes':'No'}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ marginTop:12 }}><div style={label}>Family Unit Details</div><input style={input} value={s.e.fd2} onChange={e=>ue('fd2',e.target.value)} placeholder="e.g., Debtor, spouse, and 2 dependent children" /></div>
      </div>

      {/* PROBE PANEL */}
      <div style={sect}>
        <ProbePanel section="family" probes={s.probes} updateProbe={updateProbe} />
      </div>

      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:24, paddingTop:16, borderTop:`1px solid ${S.borderLt}` }}>
        <button style={btn(S.accent,'#fff')} onClick={next} disabled={!s.e.tn||!s.e.en||!s.e.dn||!s.e.fs}>Continue →</button>
      </div>
    </div>
  );


  // ===== SCREEN 1: Income =====
  const Screen1 = () => (
    <div style={card}>
      <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:S.ink, fontFamily:F }}>Income — {s.e.rm||'Month'}</h2>
      <p style={{ margin:'4px 0 16px', fontSize:14, color:S.muted, fontFamily:F }}>All income of the bankrupt and family unit</p>
      <div style={directive}><strong>Directive 11R2-2025 ¶5:</strong> Report net amounts after statutory remittances. Self-employment requires both gross and net. Income includes wrongful dismissal damages, pay equity settlements, and workers' comp — but NOT gifts, inheritances, or windfalls.</div>
      <LineInput cats={INC} data={s.i} update={ui} />
      <div style={totalLine}>
        <div style={{ fontWeight:700, fontSize:15, color:S.ink, fontFamily:F }}>TOTAL NET MONTHLY INCOME</div>
        <div style={{ fontFamily:FM, fontWeight:700, fontSize:16, textAlign:'right', color:S.ink }}>{fmt(ti)}</div>
      </div>
      <div style={{ fontSize:11, color:S.muted, marginTop:4, fontFamily:F }}>Excludes self-employment gross (for reference only)</div>

      {/* PROBE PANEL */}
      <div style={{ marginTop: 20 }}>
        <ProbePanel section="income" probes={s.probes} updateProbe={updateProbe} />
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', marginTop:24, paddingTop:16, borderTop:`1px solid ${S.borderLt}` }}>
        <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={prev}>← Back</button>
        <button style={btn(S.accent,'#fff')} onClick={next}>Continue →</button>
      </div>
    </div>
  );


  // ===== SCREEN 2: Non-Discretionary Payments =====
  const Screen2 = () => (
    <div style={card}>
      <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:S.ink, fontFamily:F }}>Non-Discretionary Payments</h2>
      <p style={{ margin:'4px 0 16px', fontSize:14, color:S.muted, fontFamily:F }}>Payments deducted from income before surplus calculation</p>
      <div style={info}>Only mandatory statutory remittances and court-ordered/required payments count as non-discretionary. Voluntary RRSP contributions, optional benefits, and charitable giving are NOT deductible for surplus income purposes.</div>
      <LineInput cats={DED} data={s.d} update={ud} />
      <div style={totalLine}>
        <div style={{ fontWeight:700, fontSize:15, color:S.ink, fontFamily:F }}>TOTAL NON-DISCRETIONARY</div>
        <div style={{ fontFamily:FM, fontWeight:700, fontSize:16, textAlign:'right', color:S.ink }}>{fmt(td)}</div>
      </div>

      {s.e.fs && (
        <div style={{ marginTop:20, padding:16, borderRadius:8, background:sr.over200?S.warnBg:S.successBg, border:`2px solid ${sr.over200?'#fcd34d':'#a7f3d0'}` }}>
          <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color:sr.over200?S.warn:S.success, marginBottom:10, fontFamily:F }}>Surplus Income Preview</div>
          {[
            ['Total Net Income', fmt(ti)],
            ['Less: Non-Discretionary', `(${fmt(td)})`],
            ['Available Income', fmt(sr.net)],
            [`Less: LICO (${LICO[sr.sz].label})`, `(${fmt(sr.thr)})`],
          ].map(([l,v],i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0', fontFamily:F }}>
              <span style={{ color:S.text }}>{l}</span>
              <span style={{ fontFamily:FM, fontWeight:600 }}>{v}</span>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, padding:'8px 0 0', borderTop:`2px solid ${sr.over200?S.warn:S.success}`, marginTop:4, fontFamily:F }}>
            <span style={{ fontWeight:700, color:S.ink }}>Surplus / (Deficit)</span>
            <span style={{ fontFamily:FM, fontWeight:700, color:sr.over200?S.warn:S.success }}>{fmt(sr.surplus)}</span>
          </div>
          {sr.over200 && (
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, marginTop:8, fontFamily:F }}>
              <span style={{ fontWeight:700 }}>50% Payment Required</span>
              <span style={{ fontFamily:FM, fontWeight:700, color:S.warn }}>{fmt(sr.pay)}</span>
            </div>
          )}
        </div>
      )}

      {/* PROBE PANEL */}
      <div style={{ marginTop: 20 }}>
        <ProbePanel section="deductions" probes={s.probes} updateProbe={updateProbe} />
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', marginTop:24, paddingTop:16, borderTop:`1px solid ${S.borderLt}` }}>
        <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={prev}>← Back</button>
        <button style={btn(S.accent,'#fff')} onClick={next}>Continue →</button>
      </div>
    </div>
  );


  // ===== SCREEN 3: Living Expenses =====
  const Screen3 = () => (
    <div style={card}>
      <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:S.ink, fontFamily:F }}>Monthly Expenses of the Family Unit</h2>
      <p style={{ margin:'4px 0 16px', fontSize:14, color:S.muted, fontFamily:F }}>All expenses not already included in non-discretionary payments</p>
      <div style={info}>Expenses include the average monthly amount for the <strong>entire family unit</strong> — not just the debtor. Only include expenses for goods and services delivered during the bankruptcy/proposal period. Do not include payments on pre-filing unsecured debt.</div>
      <LineInput cats={EXP} data={s.x} update={ux} />
      <div style={totalLine}>
        <div style={{ fontWeight:700, fontSize:15, color:S.ink, fontFamily:F }}>TOTAL MONTHLY EXPENSES</div>
        <div style={{ fontFamily:FM, fontWeight:700, fontSize:16, textAlign:'right', color:S.ink }}>{fmt(tx)}</div>
      </div>

      {/* PROBE PANEL */}
      <div style={{ marginTop: 20 }}>
        <ProbePanel section="expenses" probes={s.probes} updateProbe={updateProbe} />
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', marginTop:24, paddingTop:16, borderTop:`1px solid ${S.borderLt}` }}>
        <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={prev}>← Back</button>
        <button style={btn(S.accent,'#fff')} onClick={next}>Continue →</button>
      </div>
    </div>
  );


  // ===== SCREEN 4: Notes =====
  const Screen4 = () => (
    <div style={card}>
      <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:S.ink, fontFamily:F }}>Notes & Documentation</h2>
      <p style={{ margin:'4px 0 16px', fontSize:14, color:S.muted, fontFamily:F }}>Document context, changes, and explanations for the Form 65 record</p>
      <div style={directive}><strong>Best practice:</strong> Notes appear on the printed Form 65 and are visible to the OSB, creditors, and Official Receiver. The consistency gate on the next screen will flag items needing explanation.</div>
      {[
        ['Income Notes', 'inc', 'Note income sources requiring explanation (e.g., variable income averaging, exempt income)...'],
        ['Expense Notes', 'exp', 'Note unusual or high expenses, dietary needs, medical conditions...'],
        ['Changes from Prior Month', 'chg', 'Describe material changes or state "No changes from prior month."'],
        ['General Notes / Other Applicable Comments', 'gen', 'Additional context for creditors, OSB, or Official Receiver. Specify if from LIT or debtor.'],
      ].map(([l,k,ph])=>(
        <div key={k} style={{ marginBottom:16 }}>
          <div style={label}>{l}</div>
          <textarea style={{ ...input, minHeight:70, resize:'vertical' }} value={s.n[k]} onChange={e=>un(k,e.target.value)} placeholder={ph} />
        </div>
      ))}

      {/* Probe completion summary */}
      {(() => {
        const allProbes = Object.values(s.probes);
        const answered = allProbes.filter(p => p.response.trim()).length;
        const total = allProbes.length;
        const doc = allProbes.filter(p => p.tag === 'DOC').length;
        const vrb = allProbes.filter(p => p.tag === 'VRB').length;
        const unv = allProbes.filter(p => p.tag === 'UNV').length;
        const na = allProbes.filter(p => p.tag === 'NA').length;
        return (
          <div style={{ padding: 16, background: S.probe, border: `1.5px solid ${S.probeBorder}`, borderRadius: 8, marginTop: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: S.probeAccent, marginBottom: 8, fontFamily: F }}>
              Epistemic Probe Summary (Internal Only)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {[
                ['Answered', answered, total, S.probeAccent],
                ['Documented', doc, answered, S.success],
                ['Verbal', vrb, answered, '#1d4ed8'],
                ['Unverified', unv, answered, S.danger],
                ['N/A', na, answered, S.muted],
              ].map(([label, count, of, color]) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: FM }}>{count}</div>
                  <div style={{ fontSize: 10, color: S.probeMuted, fontFamily: F }}>{label}</div>
                </div>
              ))}
            </div>
            {answered > 0 && (
              <div style={{ marginTop: 10, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
                {doc > 0 && <div style={{ width: `${doc/answered*100}%`, background: S.success, height: '100%' }} />}
                {vrb > 0 && <div style={{ width: `${vrb/answered*100}%`, background: '#3b82f6', height: '100%' }} />}
                {unv > 0 && <div style={{ width: `${unv/answered*100}%`, background: S.danger, height: '100%' }} />}
                {na > 0 && <div style={{ width: `${na/answered*100}%`, background: '#9ca3af', height: '100%' }} />}
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display:'flex', justifyContent:'space-between', marginTop:24, paddingTop:16, borderTop:`1px solid ${S.borderLt}` }}>
        <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={prev}>← Back</button>
        <button style={btn(S.accent,'#fff')} onClick={next}>Run Consistency Gate →</button>
      </div>
    </div>
  );


  // ===== SCREEN 5: Consistency Gate =====
  const Screen5 = () => (
    <div style={card}>
      <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:S.ink, fontFamily:F }}>Consistency Gate</h2>
      <p style={{ margin:'4px 0 16px', fontSize:14, color:S.muted, fontFamily:F }}>Rule-based checks — not AI, fully deterministic, fully traceable</p>
      <div style={info}>All flags are for <strong>your internal review only</strong>. They do not appear on the printed Form 65, CSV export, or any output visible to the OSB, creditors, or debtor. They help — they don't report.</div>

      <div style={{ padding:'14px 18px', borderRadius:8, marginBottom:20,
        background: cg.status==='pass'?S.successBg:cg.status==='review'?S.warnBg:S.dangerBg,
        border:`2px solid ${cg.status==='pass'?'#a7f3d0':cg.status==='review'?'#fcd34d':'#fecaca'}` }}>
        <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', fontFamily:F,
          color:cg.status==='pass'?S.success:cg.status==='review'?S.warn:S.danger }}>
          {cg.status==='pass'?'■ ALL CHECKS PASS':cg.status==='review'?'▪ REVIEW RECOMMENDED':'✕ ISSUES DETECTED'}
        </div>
        <div style={{ fontSize:12, color:S.text, fontFamily:F, marginTop:4 }}>{cg.fc} issues · {cg.wc} warnings · {cg.ic} suggestions</div>
      </div>

      {cg.flags.length===0 ? (
        <div style={{ textAlign:'center', padding:32, color:S.muted, fontFamily:F }}>No consistency flags. All checks pass.</div>
      ) : cg.flags.map((f,i) => (
        <div key={i} style={{ display:'flex', gap:12, padding:'14px 0', borderBottom:`1px solid ${S.borderLt}` }}>
          <div style={{ width:28, height:28, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0,
            background: f.sev==='fail'?'rgba(196,56,42,0.1)':f.sev==='warn'?'rgba(198,125,21,0.1)':'rgba(27,107,147,0.1)',
            border: `1.5px solid ${f.sev==='fail'?'rgba(196,56,42,0.3)':f.sev==='warn'?'rgba(198,125,21,0.3)':'rgba(27,107,147,0.3)'}`,
            color: f.sev==='fail'?S.danger:f.sev==='warn'?S.warn:S.accent }}>
            {f.sev==='fail'?'✕':f.sev==='warn'?'!':'i'}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:13, fontWeight:600, color:S.slate, fontFamily:F }}>{f.msg}</span>
              <span style={{ fontSize:10, fontWeight:700, fontFamily:FM, letterSpacing:'0.08em',
                color:f.sev==='fail'?S.danger:f.sev==='warn'?S.warn:S.accent }}>{f.code}</span>
            </div>
            <div style={{ fontSize:12, color:S.muted, marginTop:3, lineHeight:1.45, fontFamily:F }}>{f.det}</div>
          </div>
        </div>
      ))}

      <div style={{ display:'flex', justifyContent:'space-between', marginTop:24, paddingTop:16, borderTop:`1px solid ${S.borderLt}` }}>
        <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={prev}>← Back to Edit</button>
        <button style={btn(S.success,'#fff')} onClick={next}>Generate Report →</button>
      </div>
    </div>
  );


  // ===== SCREEN 6: Report (Print-Ready) =====
  const Screen6 = () => {
    const row = (l,v,bold) => (
      <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:14, fontFamily:F, ...(bold?{fontWeight:700,borderTop:`2px solid ${S.ink}`,paddingTop:10,marginTop:4}:{}) }}>
        <span style={{ color:bold?S.ink:S.text }}>{l}</span>
        <span style={{ fontFamily:FM, fontWeight:bold?700:600, color:bold?(sr.over200?S.warn:S.success):S.slate }}>{v}</span>
      </div>
    );

    const expCats = {};
    EXP.forEach(c => {
      if (p(s.x[c.k]) > 0) {
        if (!expCats[c.cat]) expCats[c.cat] = [];
        expCats[c.cat].push(c);
      }
    });

    return (
      <div>
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
          <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={prev}>← Back</button>
          <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={()=>{if(dm||window.confirm('Start new?')){setS(init);setScr(0);setDm(false);}}}>{dm?'Exit Demo':'New Statement'}</button>
          <button style={btn('transparent',S.text,`1.5px solid ${S.border}`)} onClick={()=>window.print()}>🖨 Print / PDF</button>
          <button style={btn('#f0edf5',S.probeAccent,`1.5px solid ${S.probeBorder}`)} onClick={() => saveCase(s, dm)}>💾 Save Case</button>
          <button style={btn('#ecfdf5',S.success,`1.5px solid #a7f3d0`)} onClick={() => exportXML(s)}>⬇ XML Export</button>
          <button style={btn('#eff6ff','#1d4ed8',`1.5px solid #bfdbfe`)} onClick={() => exportCSV(s)}>⬇ CSV Export</button>
        </div>

        <div style={{ background:S.surface, border:`1px solid ${S.border}`, borderRadius:12, padding:32 }}>
          {dm && <div style={{ textAlign:'center', padding:12, background:S.accentLt, border:`2px dashed ${S.accent}`, borderRadius:8, marginBottom:24 }}>
            <div style={{ fontSize:18, fontWeight:700, color:S.accent, letterSpacing:'0.1em', fontFamily:F }}>DEMONSTRATION ONLY</div>
          </div>}

          <div style={{ textAlign:'center', paddingBottom:20, borderBottom:`3px double ${S.ink}`, marginBottom:24 }}>
            <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:S.ink, letterSpacing:'0.03em', fontFamily:F }}>FORM 65 — MONTHLY INCOME & EXPENSE STATEMENT</h1>
            <p style={{ margin:'4px 0 0', fontSize:13, color:S.muted, fontFamily:F }}>Bankruptcy and Insolvency Act — Section 68, Directive 11R2-2025</p>
          </div>

          {/* Estate Info */}
          <div style={{ marginBottom:24 }}>
            <div style={sectTitle}>Estate Information</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 32px', marginTop:12 }}>
              {[['Trustee',s.e.tn],['License',s.e.tl],['Firm',s.e.tf],['Estate No.',s.e.en],['Debtor',s.e.dn],['Filing Date',s.e.fd],['Month',s.e.rm],['Family Unit',`${s.e.fs} persons — ${s.e.fd2}`],['Bankruptcy',s.e.ft?'First time':'Second+']].map(([l,v],i)=>(
                <div key={i} style={{ display:'flex', gap:8, fontSize:13, padding:'3px 0', fontFamily:F }}>
                  <span style={{ color:S.muted, minWidth:80 }}>{l}:</span><span style={{ fontWeight:600, color:S.slate }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Income table */}
          <div style={{ marginBottom:24 }}><div style={sectTitle}>Monthly Income</div>
            {INC.filter(c=>p(s.i[c.k])>0).map(c=>(
              <div key={c.k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${S.borderLt}`, fontSize:13, fontFamily:F }}>
                <span style={{ color:S.text }}>{c.l}</span>
                <span style={{ fontFamily:FM, fontWeight:600 }}>{fmt(p(s.i[c.k]))}</span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderTop:`2px solid ${S.ink}`, marginTop:4, fontSize:14, fontWeight:700, fontFamily:F }}>
              <span>TOTAL NET MONTHLY INCOME</span><span style={{ fontFamily:FM }}>{fmt(ti)}</span>
            </div>
          </div>

          {/* Non-Discretionary Payments */}
          <div style={{ marginBottom:24 }}><div style={sectTitle}>Non-Discretionary Payments</div>
            {DED.filter(c=>p(s.d[c.k])>0).length === 0 ? (
              <div style={{ fontSize:13, color:S.muted, fontFamily:F, padding:'8px 0' }}>No non-discretionary payments reported.</div>
            ) : DED.filter(c=>p(s.d[c.k])>0).map(c=>(
              <div key={c.k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${S.borderLt}`, fontSize:13, fontFamily:F }}>
                <span style={{ color:S.text }}>{c.l}</span>
                <span style={{ fontFamily:FM, fontWeight:600 }}>{fmt(p(s.d[c.k]))}</span>
              </div>
            ))}
            {DED.filter(c=>p(s.d[c.k])>0).length > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderTop:`2px solid ${S.ink}`, marginTop:4, fontSize:14, fontWeight:700, fontFamily:F }}>
                <span>TOTAL NON-DISCRETIONARY</span><span style={{ fontFamily:FM }}>{fmt(td)}</span>
              </div>
            )}
          </div>

          {/* Expenses by category */}
          <div style={{ marginBottom:24 }}><div style={sectTitle}>Monthly Expenses of the Family Unit</div>
            {Object.entries(expCats).map(([cat, items]) => (
              <div key={cat} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: S.accent, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 0 2px', fontFamily: F }}>{cat}</div>
                {items.map(c => (
                  <div key={c.k} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0 4px 12px', borderBottom:`1px solid ${S.borderLt}`, fontSize:13, fontFamily:F }}>
                    <span style={{ color:S.text }}>{c.l}</span>
                    <span style={{ fontFamily:FM, fontWeight:600 }}>{fmt(p(s.x[c.k]))}</span>
                  </div>
                ))}
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderTop:`2px solid ${S.ink}`, marginTop:4, fontSize:14, fontWeight:700, fontFamily:F }}>
              <span>TOTAL MONTHLY EXPENSES</span><span style={{ fontFamily:FM }}>{fmt(tx)}</span>
            </div>
          </div>

          {/* Surplus Calculation */}
          <div style={{ marginBottom:24 }}>
            <div style={sectTitle}>Surplus Income Calculation</div>
            <div style={directive}><strong>Directive 11R2-2025:</strong> If surplus exceeds $200/month, the bankrupt pays 50% of the surplus to the estate.</div>
            {row('Total Net Monthly Income of the Family Unit (Line 3)', fmt(ti))}
            {row('Less: Non-Discretionary Payments (Line 6)', `(${fmt(td)})`)}
            {row('Available Monthly Income (Line 8)', fmt(sr.net), false)}
            {row(`Less: Superintendent's Standard — ${LICO[sr.sz].label}`, `(${fmt(sr.thr)})`)}
            {row('Surplus / (Deficit)', fmt(sr.surplus), true)}
            {sr.over200 && row('Monthly Amount Required (Line 15) — 50%', fmt(sr.pay))}
            {sr.over200 && <p style={{ fontSize:12, color:S.text, marginTop:8, fontFamily:F }}>
              Surplus exceeds $200/month. Bankruptcy period {s.e.ft ? 'extends from 9 to 21 months (first-time bankrupt)' : 'extends from 24 to 36 months (second+ bankrupt)'} per 11R2 ¶7.
            </p>}
            {!sr.over200 && sr.surplus >= 0 && <p style={{ fontSize:12, color:S.success, marginTop:8, fontFamily:F }}>
              Surplus does not exceed $200/month. No surplus income obligation.
            </p>}

            {/* Monthly surplus/deficit of family unit */}
            <div style={{ marginTop: 16, padding: 12, background: '#f8f9fb', borderRadius: 6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, fontFamily:F, padding:'3px 0' }}>
                <span style={{ color:S.text }}>Available Monthly Income (Line 8)</span>
                <span style={{ fontFamily:FM, fontWeight:600 }}>{fmt(sr.net)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, fontFamily:F, padding:'3px 0' }}>
                <span style={{ color:S.text }}>Less: Total Monthly Expenses (Line 10)</span>
                <span style={{ fontFamily:FM, fontWeight:600 }}>({fmt(tx)})</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, fontFamily:F, padding:'8px 0 0', borderTop:`2px solid ${S.ink}`, marginTop:4, fontWeight:700 }}>
                <span>Monthly Surplus (Deficit) of the Family Unit (Line 11)</span>
                <span style={{ fontFamily:FM, color: (sr.net - tx) >= 0 ? S.success : S.danger }}>{fmt(sr.net - tx)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {(s.n.inc||s.n.exp||s.n.chg||s.n.gen) && <div style={{ marginBottom:24 }}>
            <div style={sectTitle}>Notes & Comments</div>
            {s.n.inc && <p style={{ fontSize:13, fontFamily:F, marginBottom:8 }}><strong>Income:</strong> {s.n.inc}</p>}
            {s.n.exp && <p style={{ fontSize:13, fontFamily:F, marginBottom:8 }}><strong>Expenses:</strong> {s.n.exp}</p>}
            {s.n.chg && <p style={{ fontSize:13, fontFamily:F, marginBottom:8 }}><strong>Changes:</strong> {s.n.chg}</p>}
            {s.n.gen && <p style={{ fontSize:13, fontFamily:F, marginBottom:8 }}><strong>Comments:</strong> {s.n.gen}</p>}
          </div>}

          {/* Scope */}
          <div style={{ padding:16, background:'#f8f5f0', border:`1px solid #e8e0d4`, borderRadius:8, marginBottom:24 }}>
            <div style={sectTitle}>Scope</div>
            <p style={{ fontSize:13, color:S.text, lineHeight:1.6, fontFamily:F, margin:0 }}>
              This statement reports income and expenses for the specified month as provided by the debtor and reviewed by the Licensed Insolvency Trustee. The surplus calculation follows the methodology prescribed by Directive No. 11R2-2025. The Superintendent's Standards are sourced from Appendix A of Directive 11R2-2025 (issued March 31, 2025).
            </p>
          </div>

          {/* Signature block */}
          <div style={{ paddingTop:20, borderTop:`1px solid ${S.border}` }}>
            <p style={{ fontSize:12, color:S.text, lineHeight:1.5, fontFamily:F, marginBottom:16 }}>
              The debtor attests that the information in this statement is full, true and complete to the best of their knowledge. The Licensed Insolvency Trustee has verified the accuracy of this income and expense statement.
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32 }}>
              <div>
                <div style={{ borderBottom:`1px solid ${S.muted}`, minWidth:200, paddingBottom:4, fontSize:13, color:S.muted, fontFamily:F, marginBottom:4 }}>Debtor Signature</div>
                <div style={{ borderBottom:`1px solid ${S.muted}`, minWidth:200, paddingBottom:4, fontSize:13, color:S.muted, fontFamily:F, marginTop:20, marginBottom:4 }}>Date &amp; Locality</div>
              </div>
              <div>
                <div style={{ borderBottom:`1px solid ${S.muted}`, minWidth:200, paddingBottom:4, fontSize:13, color:S.muted, fontFamily:F, marginBottom:4 }}>Trustee Signature</div>
                <div style={{ borderBottom:`1px solid ${S.muted}`, minWidth:200, paddingBottom:4, fontSize:13, color:S.muted, fontFamily:F, marginTop:20, marginBottom:4 }}>Date</div>
              </div>
            </div>
            <div style={{ textAlign:'right', fontSize:12, color:S.muted, fontFamily:F, marginTop:24 }}>
              <p style={{ margin:'2px 0', fontWeight:700 }}>NMA-BANKRUPTCY</p>
              <p style={{ margin:'2px 0' }}>Form 65 Navigator v2.1</p>
              <p style={{ margin:'2px 0' }}>© 2026 Dr. Olutoyese Oyelese</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const screens = [Screen0,Screen1,Screen2,Screen3,Screen4,Screen5,Screen6];
  const Current = screens[scr];

  return (
    <div style={{ fontFamily:F, background:S.bg, minHeight:'100vh' }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Return to NMA */}
      <div style={{ background: "#080c14", padding: "8px 0", borderBottom: "1px solid rgba(100,116,139,0.15)" }}><div style={{ maxWidth: 860, margin: "0 auto", padding: "0 20px" }}><a href="https://navigationalmind.com" style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b", textDecoration: "none", letterSpacing: "0.5px", display: "inline-flex", alignItems: "center", gap: 6 }}>← navigationalmind.com</a></div></div>

      {/* Header */}
      <div style={{ background:S.ink, padding:'12px 24px', borderBottom:`3px solid ${S.accent}` }}>
        <div style={{ maxWidth:860, margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:18, fontWeight:700, color:'#fff', fontFamily:F }}>NMA-Bankruptcy</div>
            <div style={{ fontSize:12, color:S.muted, fontFamily:F }}>Governance Layer for Licensed Insolvency Trustees</div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <div style={{ padding:'4px 10px', borderRadius:4, background:'rgba(107,76,154,0.2)', border:'1px solid rgba(107,76,154,0.3)', fontSize:10, fontWeight:700, color:'#c4b5e0', fontFamily:FM, letterSpacing:'0.05em' }}>PROBES</div>
            <div style={{ padding:'4px 12px', borderRadius:4, border:`1.5px solid ${S.accent}`, fontSize:11, fontWeight:700, color:S.accent, fontFamily:FM, letterSpacing:'0.05em' }}>FORM 65 · v2.1</div>
          </div>
        </div>
      </div>

      {/* Demo banner */}
      {dm && (
        <div style={{ background:`linear-gradient(135deg, ${S.accent} 0%, ${S.accentDk} 100%)`, color:'#fff', padding:'10px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ padding:'3px 10px', background:'rgba(255,255,255,0.2)', borderRadius:4, fontSize:12, fontWeight:700 }}>📋 DEMO</span>
            <span style={{ fontSize:13, opacity:0.9 }}><strong>Sarah M. Chen</strong> — EST-2026-0142 · Jan 2026 · Family of 2</span>
          </div>
          <button style={{ background:'rgba(255,255,255,0.15)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', padding:'5px 14px', borderRadius:4, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:F }}
            onClick={()=>{setS(init);setScr(0);setDm(false);}}>Exit Demo</button>
        </div>
      )}

      {/* Progress */}
      {scr < 6 && (
        <div style={{ display:'flex', justifyContent:'center', padding:'12px 16px', background:S.surface, borderBottom:`1px solid ${S.border}`, gap:4, flexWrap:'wrap' }}>
          {scrNames.map((n,i)=>(
            <div key={i} onClick={() => i < scr ? setScr(i) : null} style={{ 
              display:'flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:100,
              fontSize:12, fontWeight:i===scr?700:500, fontFamily:F,
              background:i===scr?S.accentLt:'transparent',
              color:i===scr?S.accentDk:i<scr?S.success:S.muted,
              cursor: i < scr ? 'pointer' : 'default',
            }}>
              {i<scr?'✓':i+1} <span>{n}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ maxWidth:860, margin:'0 auto', padding:'24px 16px' }}>
        <Current />
      </div>
    </div>
  );
}
